import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  useTheme,
  alpha,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  CircularProgress,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  RateReview as ReviewIcon,
} from '@mui/icons-material';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import wineService, { Wine, VintageType } from '../../../services/wineService';
import ImportExportModal from '../../../components/ImportExportModal';
import { wineConfig } from '../../../utils/importExportConfigs';

const vintageTypeLabels: Record<VintageType, string> = {
  single_vintage: 'Single Vintage',
  non_vintage: 'NV',
  multi_vintage: 'MV',
};

const WineList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [wines, setWines] = useState<Wine[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [vintageTypeFilter, setVintageTypeFilter] = useState<VintageType | 'all'>('all');
  const [reviewFilter, setReviewFilter] = useState<'all' | 'needs_review' | 'complete'>('all');
  const [importExportOpen, setImportExportOpen] = useState(false);

  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0,
  });

  const debouncedSearch = useMemo(
    () => debounce((query: string) => setSearchQuery(query), 300),
    []
  );

  const fetchWines = useCallback(async () => {
    setLoading(true);
    try {
      const needsReview =
        reviewFilter === 'needs_review' ? true : reviewFilter === 'complete' ? false : undefined;

      const response = await wineService.getAll({
        page: pagination.page + 1,
        pageSize: pagination.pageSize,
        search: searchQuery || undefined,
        vintageType: vintageTypeFilter !== 'all' ? vintageTypeFilter : undefined,
        needsReview,
      });

      // Client-side safety net
      const filtered =
        reviewFilter === 'needs_review'
          ? response.data.filter((w) => w.needsReview === true)
          : reviewFilter === 'complete'
          ? response.data.filter((w) => !w.needsReview)
          : response.data;

      setWines(filtered);
      setPagination((prev) => ({
        ...prev,
        totalItems: response.pagination.totalItems,
        totalPages: response.pagination.totalPages,
      }));
    } catch (error) {
      console.error('Failed to fetch wines:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, searchQuery, vintageTypeFilter, reviewFilter]);

  useEffect(() => {
    fetchWines();
  }, [fetchWines]);

  const handleDelete = useCallback(
    async (id: string) => {
      const wine = wines.find((w) => w._id === id);
      if (!wine) return;
      if (window.confirm(`Delete "${wine.name}"?`)) {
        try {
          await wineService.delete(id);
          fetchWines();
        } catch {
          alert('Failed to delete wine. Please try again.');
        }
      }
    },
    [wines, fetchWines]
  );

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          <TextField
            placeholder="Search wines..."
            variant="outlined"
            size="small"
            onChange={(e) => debouncedSearch(e.target.value)}
            sx={{
              flexGrow: 1,
              maxWidth: { xs: '100%', sm: 400 },
              '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.paper },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <Button size="small" onClick={() => setSearchQuery('')} sx={{ minWidth: 'auto', p: 0.5 }}>
                    <ClearIcon fontSize="small" />
                  </Button>
                </InputAdornment>
              ),
            }}
          />

          <Select
            value={vintageTypeFilter}
            onChange={(e) => setVintageTypeFilter(e.target.value as VintageType | 'all')}
            size="small"
            sx={{ minWidth: 160, '& .MuiSelect-select': { py: '6.5px', fontSize: '0.875rem' } }}
          >
            <MenuItem value="all">All Vintages</MenuItem>
            <MenuItem value="single_vintage">Single Vintage</MenuItem>
            <MenuItem value="non_vintage">Non Vintage (NV)</MenuItem>
            <MenuItem value="multi_vintage">Multi Vintage (MV)</MenuItem>
          </Select>

          <Select
            value={reviewFilter}
            onChange={(e) => setReviewFilter(e.target.value as typeof reviewFilter)}
            size="small"
            sx={{ minWidth: 130, '& .MuiSelect-select': { py: '6.5px', fontSize: '0.875rem' } }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="needs_review">Needs Review</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
          </Select>

          <Button
            variant="outlined"
            onClick={() => setImportExportOpen(true)}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            Import / Export
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/wines/create')}
            sx={{ minWidth: 'auto', px: 3 }}
          >
            Add Wine
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : wines.length === 0 ? (
          <Typography variant="body1" align="center" sx={{ p: 3 }}>
            No wines found. Click "Add Wine" to create one.
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Wine</TableCell>
                    <TableCell>Producer</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>Vintage</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {wines.map((wine) => (
                    <TableRow
                      key={wine._id}
                      hover
                      onClick={() => navigate(`/admin/wines/edit/${wine._id}`)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: wine.needsReview
                          ? alpha(theme.palette.error.main, 0.06)
                          : 'inherit',
                        '&:hover': {
                          backgroundColor: wine.needsReview
                            ? alpha(theme.palette.error.main, 0.12)
                            : 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {wine.name}
                            </Typography>
                            {!wine.active && (
                              <Chip label="Inactive" size="small" sx={{ mt: 0.5, height: 18, fontSize: '0.7rem' }} />
                            )}
                          </Box>
                          {wine.needsReview && (
                            <Tooltip title="Needs review">
                              <ReviewIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {wine.wineName?.producer?.fullName || wine.wineName?.producer?.name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: wine.wineName?.region ? 'inherit' : theme.palette.text.secondary }}>
                          {wine.wineName?.region?.name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={wine.displayVintage}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/wines/edit/${wine._id}`);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(wine._id);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={pagination.totalItems}
              page={pagination.page}
              onPageChange={(_, newPage) => setPagination((p) => ({ ...p, page: newPage }))}
              rowsPerPage={pagination.pageSize}
              onRowsPerPageChange={(e) =>
                setPagination((p) => ({ ...p, pageSize: parseInt(e.target.value, 10), page: 0 }))
              }
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>

      <ImportExportModal
        open={importExportOpen}
        onClose={() => {
          setImportExportOpen(false);
          fetchWines();
        }}
        config={wineConfig}
      />
    </Box>
  );
};

export default WineList;
